// react
import React, { useEffect, useMemo } from 'react';

// packages
import { Controller } from 'react-hook-form';

// types
import type { CountryAutocompleteProps } from 'components/CountryAutocomplete/types';

// components
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const CountryAutocomplete = function ({
  control,
  initialCountry,
  name,
  countriesList,
  label,
  disabled = false,
  setValue,
}: CountryAutocompleteProps) {
  const pinnedCountries = useMemo(() => {
    const listCopy = [...countriesList];
    const pinnedLabels = ['united states of america', 'canada'];

    const pinnedItems = pinnedLabels
      .map((label) => {
        const index = listCopy.findIndex(
          (c) => c.label.toLowerCase() === label
        );
        if (index > -1) {
          return listCopy.splice(index, 1)[0];
        }
        return null;
      })
      .filter(Boolean) as typeof countriesList;

    return [...pinnedItems, ...listCopy];
  }, [countriesList]);

  useEffect(() => {
    if (!initialCountry) return;

    // if initialCountry is object, pick `.value`; if string, just use it
    const valueToSet =
      typeof initialCountry === 'string'
        ? initialCountry
        : (initialCountry as any).value;

    setValue(name, valueToSet);
  }, [initialCountry, name, setValue]);

  if (!pinnedCountries.length) return null;

  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { value, onChange, ref, ...rest },
        fieldState: { error },
      }) => {
        // ensure autocomplete receives the full country object
        const selectedOption =
          pinnedCountries.find((c) => c.value === value) || null;

        return (
          <Autocomplete
            options={pinnedCountries}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, val) => option.value === val?.value}
            value={selectedOption}
            onChange={(_, newValue) => onChange(newValue?.value || '')}
            disabled={disabled}
            {...rest}
            renderInput={(params) => (
              <TextField
                {...params}
                autoComplete="new-password"
                variant="filled"
                label={label}
                error={!!error}
                helperText={error?.message ?? ' '}
                inputRef={ref}
                sx={{
                  '& .MuiFilledInput-input': {
                    paddingTop: '4px !important',
                    paddingBottom: '5px !important',
                  },
                }}
              />
            )}
          />
        );
      }}
    />
  );
};

export default CountryAutocomplete;
